import React, { useState, useRef } from 'react';
import {
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Box,
  Typography,
  Alert,
  ClickAwayListener,
} from '@mui/material';
import { Search as SearchIcon, LocationOn as LocationIcon } from '@mui/icons-material';
import { usePlacesSearch } from '../hooks/usePlacesSearch';
import { PlacePrediction } from '../services/placesApi';

interface PlacesAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onPlaceSelect: (placeDetails: any) => void;
  placeholder?: string;
  label?: string;
  helperText?: string;
  error?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  required?: boolean;
}

const PlacesAutocomplete: React.FC<PlacesAutocompleteProps> = ({
  value,
  onChange,
  onPlaceSelect,
  placeholder = "Start typing your address and select from suggestions",
  label = "Search Address",
  helperText = "Start typing your address and select from suggestions",
  error = false,
  disabled = false,
  fullWidth = true,
  required = false,
}) => {
  const [showPredictions, setShowPredictions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const {
    predictions,
    isLoading,
    error: apiError,
    search,
    selectPlace,
    clearPredictions,
  } = usePlacesSearch();

  // Debounce ref for search
  const debounceTimeoutRef = useRef<number | null>(null);

  // Handle input changes with debounced search
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    onChange(newValue);
    setShowPredictions(true);
    
    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    // Trigger search after debounce
    debounceTimeoutRef.current = setTimeout(() => {
      search(newValue);
    }, 300);
  };

  // Handle place selection
  const handlePlaceSelect = async (prediction: PlacePrediction) => {
    try {
      const placeDetails = await selectPlace(prediction);
      if (placeDetails) {
        onPlaceSelect(placeDetails);
        setShowPredictions(false);
      }
    } catch (error) {
      // console.error('Error selecting place:', error);
    }
  };

  // Handle input focus
  const handleInputFocus = () => {
    if (predictions.length > 0) {
      setShowPredictions(true);
    }
  };

  // Handle click away
  const handleClickAway = () => {
    setShowPredictions(false);
  };

  // Handle key down for accessibility
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setShowPredictions(false);
      clearPredictions();
    }
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ position: 'relative', width: fullWidth ? '100%' : 'auto' }}>
        <TextField
          ref={inputRef}
          fullWidth={fullWidth}
          label={label}
          placeholder={placeholder}
          value={value}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          helperText={apiError ? apiError : helperText}
          error={error || !!apiError}
          disabled={disabled}
          required={required}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            endAdornment: isLoading ? (
              <CircularProgress size={20} />
            ) : null,
          }}
        />

        {/* Predictions Dropdown */}
        {showPredictions && (predictions.length > 0 || isLoading) && (
          <Paper
            ref={listRef}
            elevation={4}
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 1000,
              maxHeight: 300,
              overflow: 'auto',
              mt: 0.5,
              borderRadius: 2,
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {isLoading ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <CircularProgress size={20} />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Searching places...
                </Typography>
              </Box>
            ) : predictions.length > 0 ? (
              <List dense sx={{ py: 0.5 }}>
                {predictions.map((prediction, index) => (
                  <ListItem
                    key={prediction.place_id || index}
                    component="button"
                    onClick={() => handlePlaceSelect(prediction)}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 1,
                      mx: 0.5,
                      my: 0.25,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'primary.main',
                        color: 'primary.contrastText',
                        '& .MuiListItemText-primary': {
                          fontWeight: 600,
                        },
                        '& .MuiSvgIcon-root': {
                          color: 'primary.contrastText',
                        },
                      },
                    }}
                  >
                    <LocationIcon sx={{ mr: 1.5, color: 'text.secondary', fontSize: 20 }} />
                    <ListItemText
                      primary={prediction.structured_formatting?.main_text || prediction.description || 'Unknown address'}
                      secondary={prediction.structured_formatting?.secondary_text || ''}
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: 500,
                      }}
                      secondaryTypographyProps={{
                        variant: 'caption',
                        color: 'text.secondary',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            ) : null}
          </Paper>
        )}

        {/* API Error Alert */}
        {apiError && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            <Typography variant="body2">
              Address search is temporarily unavailable. You can still enter your address manually below.
            </Typography>
            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
              Error: {apiError}
            </Typography>
          </Alert>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default PlacesAutocomplete;
